import {Feature, FeatureCollection} from '@turf/turf';
import {LayerDatasource, LayerLoadType, MapperViewer} from '../../types';
import {PlanEntity} from '../../../entities/plan.entity';
import {IgoService} from '../../../../services/igo.service';
import {drawSvg} from '../../cesiumTools';
import {getIconSize, isENJEUX, setIconSize, sortPriority} from '../../../tools';
import {BillboardCollection} from 'cesium';
import {createPlanLabel, createPlanLine, createPlanPicto, setPlanBillboardSizes} from '../../planTools';
import { MapperService } from 'src/services/mapper.service';

declare const Cesium;

export class PlanLayer implements LayerDatasource {

  id = 'picto';
  name = 'Plan d\'actions';
  active = true;
  dataSource = new Cesium.GeoJsonDataSource(this.name);
  loadType = LayerLoadType.Instant;
  viewer: MapperViewer;

  /* Custom properties */
  iconSize = 40;

  entities = new Map<string, PlanEntity>();
  billboardCollection: BillboardCollection = null;
  geojson: FeatureCollection = null;
  uuidPlan: string = null;

  constructor(
    public mapperService: MapperService,
  ) {}

  onBeforePrint(): void {
    // Pour l'impression, on ajuste la taille des pictos/labels pour convenir à une résolution
    // finale de 1500px de large pour les screenshots 3D

    const diffBetweenScreenAndScreenshotSize = (1500 * 100 / this.viewer.canvasWidth);
    const computedIconSize = (this.iconSize * diffBetweenScreenAndScreenshotSize) / 100;

    setPlanBillboardSizes(this.billboardCollection, computedIconSize);
    this.viewer.scene.requestRender();
  }
  onAfterPrint(): void {
    // Après impression, on remet les tailles d'icônes/labels comme avant..

    setPlanBillboardSizes(this.billboardCollection, this.iconSize);
    this.viewer.scene.requestRender();
  }

  public updateIconSize(iconSize: number): void {
    if (iconSize < 10 || iconSize > 60) {
      return;
    }

    setPlanBillboardSizes(this.billboardCollection, iconSize);
    setIconSize(this.viewer, this.iconSize);
    this.viewer.scene.requestRender();
  }

  initialize(viewer: MapperViewer): void {
    this.viewer = viewer;
    this.viewer.dataSources.add(this.dataSource);

    // Pour palier des problèmes de rendu de picto qui se superposent, on passe par une BillboardCollection
    // qui permet de gérer le blending/translucency
    this.billboardCollection = new Cesium.BillboardCollection({
      scene: this.viewer.scene,
      blendOption: Cesium.BlendOption.TRANSLUCENT,
    });

    this.viewer.scene.primitives.add(this.billboardCollection);

    this.iconSize = getIconSize(viewer);
  }

  remove(): void {
    this.viewer.dataSources.remove(this.dataSource);
  }

  loadData(): void {
    if (!this.dataSource) {
      console.error('Viewer not initialized');
      return;
    }

    this.mapperService.getGeoJsonPlan(this.viewer.plan).subscribe((geojson) => {
      this.geojson = geojson;

      this.buildPlan();
    });
  }


  public toggleEntity(key, show): void {
    const len = this.billboardCollection.length;
    for (let i = 0; i < len; ++i) {
      const b = this.billboardCollection.get(i);

      if (b.id.includes(key)) {
        b.show = show;
      }
    }

    const polyline = this.dataSource.entities.getById(`line_${key}`);

    if (polyline) {
      polyline.show = show;
    }

    this.entities.get(key).show = show;

    this.viewer.scene.requestRender();
  }


  buildPlan = () => {
    const pointsToPlace: Array<{ feature: Feature; entity: PlanEntity }> = [];

    this.geojson?.features?.forEach(feature => {


      let entity = null;
      if (!this.entities.has(feature.properties.fid)) {
        entity = new PlanEntity(feature.properties.num);

        entity.setFilter('CATEGORIE', feature.properties.alias, true);

        if (isENJEUX(feature.properties)) {
          entity.setFilter('PRIORITÉ', feature.properties.priority || 'Enjeux et actions', true, sortPriority(feature.properties.priority));
        } else {
          entity.setFilter('PRIORITÉ', 'Moyens et gestion de crise', true, 5);
        }

        if (feature.properties.competence) {
          entity.setFilter('COMPÉTENCE', feature.properties.competence, true);
        }

        if (feature.properties.type_alea) {
          entity.setFilter('TYPE ALÉA', feature.properties.type_alea, true);
        }

        this.entities.set(feature.properties.fid, entity);
      } else {
        entity = this.entities.get(feature.properties.fid);
      }

      if (feature.geometry.type === 'LineString') {
        const line = createPlanLine(feature);

        this.dataSource.entities.add(line);
      }

      if (feature.geometry.type === 'Point') {
        pointsToPlace.push({feature, entity});
      }
    });

    this.placePictos(pointsToPlace);

    this.viewer.scene.primitives.raiseToTop(this.billboardCollection);

    this.viewer.dataSources.raiseToTop(this.dataSource);
  }

  // Avec heightReference (clamp), les pictos "flottent" tant que la tuile de terrain
  // couvrant leur position n'est pas encore chargée. On échantillonne donc la
  // hauteur une seule fois en amont et on la fige dans la position, indépendamment du chargement du terrain.
  private placePictos(points: Array<{ feature: Feature; entity: PlanEntity }>): void {
    if (!points.length) {
      return;
    }

    const cartographics = points.map(({feature}) => {
      const coordinates = (feature.geometry as any).coordinates;

      return Cesium.Cartographic.fromDegrees(Number(coordinates[0]), Number(coordinates[1]));
    });

    Cesium.sampleTerrainMostDetailed(this.viewer.terrainProvider, cartographics)
      .catch(() => cartographics)
      .then((sampled) => {
      points.forEach(({feature, entity}, i) => {
        const height = sampled[i].height;
        const picto = createPlanPicto(feature, this.iconSize, this.viewer, height);

        this.billboardCollection.add(picto);
        entity.coordinates.push((feature.geometry as any).coordinates.map((c) => Number(c)));

        drawSvg(String(feature.properties.num), feature.properties.priority).then(img => {
          const label = createPlanLabel(feature, img, this.iconSize, this.viewer, height);

          this.billboardCollection.add(label);
          this.viewer.scene.requestRender();
        });
      });

      this.viewer.scene.requestRender();
    });
  }

  setActive(active: boolean): void {
    this.active = active;

    // Pictos and labels
    this.billboardCollection.show = active;

    // Polylines
    this.dataSource.show = active;

    this.viewer.scene.requestRender();
  }
}
